<script setup lang="ts">
/**
 * Черновик крупного действия (макеты 8-3, 8-3a, 8-4).
 *
 * Один блок на все крупные действия, а не свой экран на каждое: человек читает
 * «что сейчас произойдёт» в одном и том же месте разговора, и решение всегда
 * под одной и той же парой кнопок. Ход на сервере уже стоит — до нажатия
 * ничего не исполняется.
 *
 * Показывается ровно то, с чем инструмент позовут: пачка задач — таблицей,
 * переписывание текста — сравнением с тем, что в задаче сейчас. Незнакомый
 * инструмент доезжает сырыми аргументами: показать нечем, но подтвердить его
 * человек всё равно обязан, иначе разговор встанет насмерть.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AppButton } from '@vinprop/design-system'

import type { V1IssueType, V1Priority } from '@/api/clients/tracker-issue'
import { getIssueByKey } from '@/features/issue/api'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import type { PanelDraft } from '@/stores/assistant'

import { describeDraft, diffHunks, type DiffLine } from './draft'
import { formatDetails } from './tools'

const props = defineProps<{ item: PanelDraft; busy: boolean }>()

const emit = defineEmits<{ decide: [approved: boolean] }>()

const { t } = useI18n()

const view = computed(() => describeDraft(props.item.name, props.item.args))

const isPending = computed(() => props.item.state === 'pending')

/** Значения инструментов — строчные: `task`, `medium`. Словарь — по enum'ам. */
const issueType = (type: string) => `ISSUE_TYPE_${type.toUpperCase()}` as V1IssueType
const priorityKey = (priority: string) => `PRIORITY_${priority.toUpperCase()}` as V1Priority

/**
 * Как выглядит задача сейчас.
 *
 * Дозапрос, а не поле события: в аргументах вызова лежит только новый текст, а
 * без старого сравнение превращается в «весь текст добавлен» — по нему не
 * увидеть, что именно модель переписала.
 */
const before = ref<{ title: string; description_md: string } | null>(null)

watch(
  () => (view.value.kind === 'fields' ? view.value.issueKey : ''),
  async (key) => {
    if (!key) return

    try {
      const issue = await getIssueByKey(key)

      before.value = issue
        ? { title: issue.title, description_md: issue.descriptionMd }
        : { title: '', description_md: '' }
    } catch {
      // Задача не прочиталась — сравнивать не с чем, и черновик показывает
      // только новый текст. Кнопки при этом работают: решение человека от
      // доступности старого текста не зависит.
      before.value = { title: '', description_md: '' }
    }
  },
  { immediate: true },
)

const fields = computed(() => {
  if (view.value.kind !== 'fields') return []

  return view.value.fields.map((field) => ({
    name: field.name,
    hunks: diffHunks(before.value?.[field.name] ?? '', field.after),
  }))
})

/** Подпись слева: «черновик · 6 задач», «черновик · описание». */
const caption = computed(() => {
  const badge = t('assistant.draft.badge')

  if (view.value.kind === 'issues') {
    return `${badge} · ${t('assistant.tool.issues', view.value.issues.length)}`
  }

  if (view.value.kind === 'fields') {
    return `${badge} · ${view.value.fields.map((field) => t(`assistant.draft.field.${field.name}`)).join(', ')}`
  }

  return `${badge} · ${props.item.name}`
})

/** Подпись справа: куда ляжет пачка или какую задачу правят. */
const subject = computed(() => {
  if (view.value.kind === 'issues') {
    return view.value.epicKey
      ? t('assistant.draft.inEpic', { key: view.value.epicKey })
      : view.value.projectKey
  }

  return view.value.kind === 'fields' ? view.value.issueKey : ''
})

const confirmLabel = computed(() =>
  view.value.kind === 'issues'
    ? t('assistant.draft.create', view.value.issues.length)
    : t('assistant.draft.apply'),
)

const sign = (item: DiffLine) => (item.added ? '+' : '-')
</script>

<template>
  <section class="draft" :class="{ 'draft--settled': !isPending }">
    <header class="draft__head">
      <span class="draft__caption">{{ caption }}</span>
      <span v-if="subject" class="draft__subject" :class="{ 'draft__subject--key': view.kind === 'fields' }">
        {{ subject }}
      </span>
    </header>

    <!-- Таблица пачки: по строке на задачу, как в списке задач. -->
    <ul v-if="view.kind === 'issues'" class="draft__rows">
      <li v-for="(issue, index) in view.issues" :key="index" class="draft__row">
        <IssueTypeIcon :type="issueType(issue.type)" />

        <span class="draft__title">{{ issue.title }}</span>
        <span class="draft__priority">{{ t(`issues.priorities.${priorityKey(issue.priority)}`) }}</span>
      </li>
    </ul>

    <div v-else-if="view.kind === 'fields'" class="draft__diff">
      <template v-for="field in fields" :key="field.name">
        <div v-for="(hunk, index) in field.hunks" :key="index" class="draft__hunk">
          <p
            v-for="(item, position) in hunk"
            :key="position"
            class="draft__line"
            :class="item.added ? 'draft__line--added' : 'draft__line--removed'"
          >
            <span class="draft__sign" aria-hidden="true">{{ sign(item) }}</span>
            <span class="draft__text">{{ item.text }}</span>
          </p>
        </div>
      </template>
    </div>

    <pre v-else class="draft__raw">{{ formatDetails(item.args) }}</pre>

    <footer class="draft__foot">
      <template v-if="isPending">
        <AppButton size="small" variant="secondary" :disabled="busy" @click="emit('decide', false)">
          {{ t('assistant.draft.decline') }}
        </AppButton>

        <AppButton size="small" :disabled="busy" @click="emit('decide', true)">
          {{ confirmLabel }}
        </AppButton>
      </template>

      <span v-else class="draft__settled">
        {{ item.state === 'applied' ? t('assistant.draft.applied') : t('assistant.draft.declined') }}
      </span>
    </footer>
  </section>
</template>

<style scoped>
.draft {
  container-type: inline-size;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-app);
}

/* Решённый черновик остаётся в разговоре, но не тянет на себя внимание. */
.draft--settled {
  opacity: 0.7;
}

.draft__head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px;
  border-bottom: 1px solid var(--color-border);
}

.draft__caption {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: uppercase;
}

.draft__subject {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.draft__subject--key {
  font-family: var(--font-family-mono);
  font-weight: var(--font-weight-medium);
}

.draft__rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.draft__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 6px 10px;
}

.draft__row + .draft__row {
  border-top: 1px solid var(--color-border);
}

.draft__title {
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  overflow-wrap: anywhere;
}

.draft__priority {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

/* Узкая панель (макет 8-3a): приоритет уступает место заголовку задачи. */
@container (max-width: 320px) {
  .draft__priority {
    display: none;
  }
}

.draft__diff {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 0;
}

.draft__line {
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 3px 10px;
  font-size: var(--font-size-body-sm);
  line-height: 18px;
}

.draft__line--removed {
  background: var(--color-danger-tint);
  color: var(--color-text-muted);
}

.draft__line--added {
  background: var(--color-success-tint);
  color: var(--color-text-primary);
}

.draft__sign {
  flex-shrink: 0;
  width: 8px;
  font-family: var(--font-family-mono);
  line-height: 20px;
}

.draft__line--removed .draft__sign {
  color: var(--color-danger-strong);
}

.draft__line--added .draft__sign {
  color: var(--color-success-strong);
}

.draft__text {
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.draft__raw {
  max-height: 200px;
  margin: 0;
  padding: 8px 10px;
  overflow: auto;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.draft__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
}

.draft__settled {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
